import * as fontkit from 'fontkit'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { BlendMode, PDFDocument, rgb } from 'pdf-lib'
import QRCode from 'qrcode'
import React, { useEffect, useRef, useState } from 'react'

const QRCodePDF = ({ assignmentCode }: { assignmentCode: string }) => {
  const qrCodeRef = useRef<HTMLDivElement>(null)
  const qrText = assignmentCode.normalize('NFC')
  const [qrDataUrl, setQrDataUrl] = useState('')

  useEffect(() => {
    const generateQr = async () => {
      const dataUrl = await QRCode.toDataURL(qrText, {
        errorCorrectionLevel: 'M',
        margin: 1,
        width: 150
      })
      setQrDataUrl(dataUrl)
    }

    generateQr().catch((error) => {
      console.error('Error generating QR code', error)
    })
  }, [qrText])

  const generatePdf = async () => {
    const qrCodeElement = qrCodeRef.current

    if (!qrCodeElement) {
      alert('QR code not found!')
      return
    }

    const canvas = await html2canvas(qrCodeElement)
    const imgData = canvas.toDataURL('image/png')

    const pdf = new jsPDF('portrait', 'px', [210, 210])
    pdf.addImage(imgData, 'PNG', 10, 10, 190, 190)
    pdf.save(`QRCode-${assignmentCode}.pdf`)
  }

  const generatePdfWithQrOverlay = async () => {
    try {
      const existingPDFUrl = '/pdfs/instrukcja-aplikacji-kod-dietetyka.pdf'
      const existingPdfBytes = await fetch(existingPDFUrl).then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to fetch PDF file: ${res.statusText}`)
        }
        return res.arrayBuffer()
      })

      const qrCodeElement = qrCodeRef.current

      if (!qrCodeElement) {
        alert('QR code not found!')
        return
      }

      const canvas = await html2canvas(qrCodeElement)
      const qrCodeImage = canvas.toDataURL('image/png')

      const pdfDoc = await PDFDocument.load(existingPdfBytes)
      pdfDoc.registerFontkit(fontkit)

      const fontUrl = '/fonts/Roboto-Regular.ttf'
      const fontBytes = await fetch(fontUrl).then((res) => res.arrayBuffer())
      const customFont = await pdfDoc.embedFont(fontBytes)

      const [firstPage] = pdfDoc.getPages()

      const qrImageBytes = await fetch(qrCodeImage).then((res) =>
        res.arrayBuffer()
      )
      const qrImage = await pdfDoc.embedPng(qrImageBytes)

      // QR code size (you can change these values as desired)
      const qrCodeWidth = 80
      const qrCodeHeight = 80

      const xPositionQRCode = 120
      const yPositionQRCode = 194

      // Draw the QR code on the page
      firstPage.drawImage(qrImage, {
        x: xPositionQRCode,
        y: yPositionQRCode,
        width: qrCodeWidth,
        height: qrCodeHeight,
        blendMode: BlendMode.Normal
      })

      // Step 5: Add assignmentCode text below the QR code
      const fontSize = 16
      const textColor = rgb(0, 0, 0)

      const xPositionText = 208
      const yPositionText = 226

      // Draw the text with drawText method
      firstPage.drawText(assignmentCode, {
        x: xPositionText,
        y: yPositionText,
        size: fontSize,
        color: textColor,
        font: customFont
      })

      const modifiedPdfBytes = await pdfDoc.save()
      const blob = new Blob([new Uint8Array(modifiedPdfBytes)], {
        type: 'application/pdf'
      })
      const fileURL = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = fileURL
      link.download = `QRCode-${assignmentCode}.pdf`
      link.click()
    } catch (error) {
      console.error('Error generating PDF', error)
    }
  }

  return (
    <div className="p-8 bg-gray-100 dark:bg-gray-900 flex flex-col items-center space-y-4">
      <div
        ref={qrCodeRef}
        className="p-8 bg-white shadow-md rounded-lg flex flex-col items-center"
      >
        {qrDataUrl ? (
          <img src={qrDataUrl} alt="QR Code" width={180} height={180} />
        ) : (
          <div style={{ width: 150, height: 150 }} />
        )}
      </div>

      <button
        onClick={generatePdf}
        className="mt-6 px-6 py-3 bg-blue-500 text-white text-lg font-medium rounded hover:bg-blue-600"
      >
        Wydrukuj kod QR
      </button>

      <button
        onClick={generatePdfWithQrOverlay}
        className="mt-6 px-6 py-3 bg-blue-500 text-white text-lg font-medium rounded hover:bg-blue-600"
      >
        Wydrukuj instrukcję
      </button>
    </div>
  )
}

export default QRCodePDF
